import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Merge, ArrowRight, Trash2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function MergeDuplicates({ onMerged }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [done, setDone] = useState(null);

  const openPreview = async () => {
    setOpen(true); setPlan(null); setDone(null); setLoading(true);
    try {
      const { data } = await api.get("/producten/merge-duplicates/preview");
      setPlan(data);
    } catch {
      toast.error("Kon preview niet laden");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const execute = async () => {
    setExecuting(true);
    try {
      const { data } = await api.post("/producten/merge-duplicates");
      setDone(data);
      toast.success(`${data.samengevoegd} samengevoegd · ${data.verwijderd} verwijderd`);
      onMerged?.();
    } catch {
      toast.error("Samenvoegen mislukt");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={openPreview} data-testid="merge-duplicates-btn">
        <Merge className="h-4 w-4 mr-1" /> Dubbele SKU's samenvoegen
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setPlan(null); setDone(null); } }}>
        <DialogContent className="max-w-3xl" data-testid="merge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="h-5 w-5 text-primary" /> Dubbele SKU's samenvoegen
            </DialogTitle>
          </DialogHeader>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Preview laden…
            </div>
          )}

          {done && (
            <div className="space-y-2 text-sm py-6 text-center" data-testid="merge-done">
              <Check className="h-8 w-8 text-primary mx-auto" />
              <div className="font-medium text-base">Klaar</div>
              <div className="text-muted-foreground">{done.samengevoegd} producten samengevoegd · {done.verwijderd} varianten verwijderd.</div>
            </div>
          )}

          {!loading && !done && plan && (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                De Nederlandse variant blijft behouden; de vreemde-taalnaam wordt in <strong>naam_en</strong> (NB) of <strong>naam_fr</strong> (FR) gezet,
                voorraad wordt opgeteld en de variant wordt verwijderd. <strong>Dit kan niet ongedaan worden gemaakt.</strong>
              </p>
              <div className="flex gap-2">
                <Badge variant="outline">{plan.aantal_groepen} samen te voegen</Badge>
                <Badge variant="outline" className="text-destructive border-destructive/40">{plan.aantal_te_verwijderen} te verwijderen</Badge>
                {plan.overgeslagen?.length > 0 && <Badge variant="outline" className="text-amber-600 border-amber-300">{plan.overgeslagen.length} overgeslagen</Badge>}
              </div>

              <div className="border border-border rounded-lg overflow-y-auto max-h-[46vh] divide-y divide-border" data-testid="merge-plan-list">
                {plan.samenvoegen.map((a) => (
                  <div key={a.sku} className="p-3 space-y-1.5" data-testid={`merge-row-${a.sku}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs bg-secondary px-1.5 py-0.5 rounded">{a.sku}</span>
                      <span className="font-medium truncate">{a.keep.naam}</span>
                    </div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-0.5">
                      {a.naam_en && <span>🇬🇧 naam_en: <span className="text-foreground">{a.naam_en}</span></span>}
                      {a.naam_fr && <span>🇫🇷 naam_fr: <span className="text-foreground">{a.naam_fr}</span></span>}
                      <span>voorraad: {a.stock_before.wh + a.stock_before.lev + a.stock_before.china} <ArrowRight className="h-3 w-3 inline" /> <span className="text-foreground font-medium">{a.stock_after.wh + a.stock_after.lev + a.stock_after.china}</span></span>
                    </div>
                    {a.delete.map((d) => (
                      <div key={d.id} className="text-xs text-destructive/80 flex items-center gap-1 ml-1">
                        <Trash2 className="h-3 w-3 shrink-0" /> [{d.taal}] {d.naam}
                      </div>
                    ))}
                  </div>
                ))}
                {plan.samenvoegen.length === 0 && (
                  <div className="p-6 text-center text-muted-foreground">Geen dubbele SKU's gevonden om samen te voegen.</div>
                )}
              </div>

              {plan.overgeslagen?.length > 0 && (
                <div className="text-xs text-muted-foreground border border-border rounded p-2 max-h-28 overflow-y-auto">
                  <div className="font-medium mb-1">Overgeslagen:</div>
                  {plan.overgeslagen.map((s, i) => <div key={i}>{s.sku}: {s.reden}</div>)}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {done ? (
              <Button onClick={() => setOpen(false)} data-testid="merge-close">Sluiten</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
                <Button onClick={execute} disabled={executing || loading || !plan || plan.aantal_groepen === 0}
                  className="bg-destructive hover:bg-destructive/90 text-destructive-foreground" data-testid="merge-confirm">
                  {executing ? "Bezig…" : `Samenvoegen & verwijderen (${plan?.aantal_te_verwijderen || 0})`}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
